import { useState } from "react";
import { BrandLogo } from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { login, signup } from "../lib/api";
import { setSession } from "../lib/session";

interface StatusMessage {
  text: string;
  tone: "error" | "success" | null;
}

const emptyStatus: StatusMessage = { text: "", tone: null };

function statusClasses(status: StatusMessage) {
  const base = "status min-h-[1.2rem] text-[0.95rem]";
  if (!status.text) {
    return `${base} text-muted`;
  }
  if (status.tone === "error") {
    return `${base} visible error text-danger`;
  }
  if (status.tone === "success") {
    return `${base} visible success text-success`;
  }
  return `${base} visible text-muted`;
}

export function AuthPage() {
  const [loginStatus, setLoginStatus] = useState<StatusMessage>(emptyStatus);
  const [signupStatus, setSignupStatus] = useState<StatusMessage>(emptyStatus);

  return (
    <div id="auth-page" className="flex flex-col items-center justify-center min-h-screen pb-[12vh]">
      <div className="w-[min(1120px,calc(100%-2rem))] grid grid-cols-[minmax(0,1.15fr)_minmax(320px,420px)] gap-5 max-[1100px]:grid-cols-1 max-[720px]:w-[min(100%,calc(100%-1rem))]">
        <section className="panel-surface auth-copy-bg rounded-panel p-10 relative overflow-hidden">
          <div className="brand inline-flex items-center gap-[0.7rem] text-[1.45rem] font-bold tracking-[-0.03em]">
            <BrandLogo />
            <span>Lustbucket</span>
          </div>
          <h1 className="mt-6 mb-4 text-[clamp(2.2rem,5vw,3.8rem)] leading-[0.92] tracking-[-0.06em]">
            A scrapbook for everything that catches your eye.
          </h1>
          <p className="text-muted">
            Save the products, images, and ideas you don't want to lose. Lustbucket quietly sorts them into boards you'll actually come back to.
          </p>
          <ul className="mt-[1.4rem] pl-0 text-muted space-y-2 list-none">
            <li className="flex gap-3">
              <span aria-hidden className="mt-[0.55rem] h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
              <span>Paste a link, drop in an image, or just describe a mood — it all gets a home.</span>
            </li>
            <li className="flex gap-3">
              <span aria-hidden className="mt-[0.55rem] h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
              <span>Boards group your finds by theme, aesthetic, or whatever thread connects them.</span>
            </li>
            <li className="flex gap-3">
              <span aria-hidden className="mt-[0.55rem] h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
              <span>Browse, revisit, or share your collections whenever inspiration strikes.</span>
            </li>
          </ul>
          <div className="mt-8 flex flex-wrap gap-2 text-xs text-muted/80">
            {["linen & living", "vintage hi-fi", "cabin aesthetics", "understated watches", "handmade tools"].map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-accent-ring/40 bg-accent-soft/40 px-3 py-1"
              >
                {tag}
              </span>
            ))}
          </div>
        </section>

        <section className="panel-surface rounded-panel p-6">
          <Tabs defaultValue="login">
            <TabsList aria-label="Authentication">
              <TabsTrigger value="login" data-tab="login">Login</TabsTrigger>
              <TabsTrigger value="signup" data-tab="signup">Signup</TabsTrigger>
            </TabsList>

            <Button variant="default" asChild className="flex items-center justify-center gap-2 min-h-[2.8rem] w-full mt-1 mb-2">
              <a href="/api/auth/google">
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58Z" fill="#EA4335"/>
                </svg>
                Sign in with Google
              </a>
            </Button>

            <TabsContent value="login">
              <form
                id="login-form"
                className="grid gap-[0.9rem]"
                onSubmit={async (event) => {
                  event.preventDefault();
                  setLoginStatus(emptyStatus);
                  const form = event.currentTarget;
                  const email = (form.elements.namedItem("login-email") as HTMLInputElement).value;
                  const password = (form.elements.namedItem("login-password") as HTMLInputElement).value;
                  const result = await login(email, password);
                  if (!result.ok) {
                    setLoginStatus({ text: result.error || "Failed to login", tone: "error" });
                    return;
                  }
                  setSession(result.data.id, result.data.email);
                }}
              >
                <Input id="login-email" name="login-email" type="email" placeholder="Email" required />
                <Input id="login-password" name="login-password" type="password" placeholder="Password" required />
                <Button variant="primary" size="lg" type="submit">
                  Login
                </Button>
                <p id="login-message" className={statusClasses(loginStatus)}>
                  {loginStatus.text}
                </p>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form
                id="signup-form"
                className="grid gap-[0.9rem]"
                onSubmit={async (event) => {
                  event.preventDefault();
                  setSignupStatus(emptyStatus);
                  const form = event.currentTarget;
                  const email = (form.elements.namedItem("signup-email") as HTMLInputElement).value;
                  const password = (form.elements.namedItem("signup-password") as HTMLInputElement).value;
                  const username = (form.elements.namedItem("signup-username") as HTMLInputElement).value;
                  const result = await signup(email, password, username);
                  if (!result.ok) {
                    setSignupStatus({ text: result.error || "Failed to create account", tone: "error" });
                    return;
                  }
                  setSession(result.data.id, result.data.email);
                }}
              >
                <Input id="signup-username" name="signup-username" type="text" placeholder="Username" required />
                <Input id="signup-email" name="signup-email" type="email" placeholder="Email" required />
                <Input id="signup-password" name="signup-password" type="password" placeholder="Password" required minLength={8} />
                <Button variant="primary" size="lg" type="submit">
                  Create account
                </Button>
                <p id="signup-message" className={statusClasses(signupStatus)}>
                  {signupStatus.text}
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </section>
      </div>
    </div>
  );
}
